/**
 * Measurement model for the SNYPER-5G import (design 2026-10-12, Teilprojekt A).
 *
 * Three levels:
 * 1. `MeasurementSeries` - one visit on site; one import creates one series.
 *    A re-measurement is a new series (answer 3.5).
 * 2. `MeasurementPoint`  - one numbered point within a series. Numbers run
 *    from 1 per series, ordered by the device timestamp (answer 2.5).
 * 3. `Measurement`       - the imported file of that point: every cell the
 *    device reported. The rating is NEVER stored - it is derived on read, so a
 *    threshold change shows immediately; documents freeze it when published.
 */
export const RADIO_TECHNOLOGIES = ['4G', '5G', 'LTE-M'];
export const MEASUREMENT_SERIES_STATUSES = ['importing', 'complete'];
export const MEASUREMENT_POINT_STATUSES = ['measured', 'obsolete'];
/**
 * RSRP thresholds in dBm (answer 1.16, 2026-10-12): the former lowest limit
 * -85 became -90, the others stayed. A value ON a limit belongs to the better
 * level ("-65 bis unter -50" is level 4).
 */
export const RSRP_THRESHOLDS = { level5: -50, level4: -65, level3: -80, level2: -90 };
export function rateRsrp(rsrp) {
    if (rsrp >= RSRP_THRESHOLDS.level5)
        return 5;
    if (rsrp >= RSRP_THRESHOLDS.level4)
        return 4;
    if (rsrp >= RSRP_THRESHOLDS.level3)
        return 3;
    if (rsrp >= RSRP_THRESHOLDS.level2)
        return 2;
    return 1;
}
/**
 * One entry per technology x provider, in the given order - also where the
 * provider has no cell at this point, so the gap is visible as `---`. A phone
 * camps on the strongest cell, hence the maximum RSRP decides.
 */
export function ratePoint(cells, technologies, providers) {
    const ratings = [];
    for (const technology of technologies) {
        for (const provider of providers) {
            let best = null;
            for (const candidate of cells) {
                if (candidate.technology !== technology || candidate.provider !== provider)
                    continue;
                if (candidate.rsrp === null)
                    continue;
                if (best === null || candidate.rsrp > best)
                    best = candidate.rsrp;
            }
            ratings.push({
                technology,
                provider,
                rsrp: best,
                level: best === null ? null : rateRsrp(best),
            });
        }
    }
    return ratings;
}
/** Union of all providers of a series, sorted, so every point shows the same columns. */
export function collectProviders(measurements) {
    const providers = new Set();
    for (const measurement of measurements) {
        for (const entry of measurement.cells)
            providers.add(entry.provider);
    }
    return [...providers].sort((a, b) => a.localeCompare(b, 'de'));
}
//# sourceMappingURL=measurement.js.map